import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Anchor, Flex, Heading, InlineFeedback, mediaMixin, Paragraph, spaceMixin } from '@zillow/constellation';

import { uiApplicationStatusTypes } from '../../../../../constants/statusConstants';
import { NO_COMPLETED_APPLICATIONS_MESSAGE } from '../../../../../constants/messages';

import InviteToApplyLinks from '../../../../../components/InviteToApplyLinks/InviteToApplyLinks';
import { URLS } from '../../../../../constants/urls';
import ListingApplicationCard from './ListingApplicationsCardList/ListingApplicationCard';
import { emitSendApplicationLinkEvent } from '../../../../../../client/analytics/clickstreamEvents';
import { useListingUser } from '../../../../../hooks/useListingUser';

const ApplicationsListTitle = styled(Flex)`
  padding-bottom: ${spaceMixin('sm')};

  ${mediaMixin(
    'lg',
    css`
      padding-bottom: ${spaceMixin('lg')};
    `,
  )}
`;

const ListingApplicationsCardListItem = styled.li`
  list-style: none;
  padding-top: ${spaceMixin('sm')};
  padding-bottom: ${spaceMixin('sm')};
  border-top: 1px solid ${(props) => props.theme.constellation.colors.gray300};
  &:last-child {
    border-bottom: 1px solid ${(props) => props.theme.constellation.colors.gray300};
  }
`;

export default function ListingApplicationsApplicationSection({ applications, applicationMap, sectionType, titleCta }) {
  const isInvitedSection = sectionType === uiApplicationStatusTypes.INVITED;
  const isCompletedSection = sectionType === uiApplicationStatusTypes.COMPLETED;
  const { user, listing } = useListingUser();

  const applicationsExist = applications?.length > 0;
  // Ensure applications loaded before showing no applications message.
  const applicationsDoNotExist = applications?.length === 0;

  // Always show the section for invited applicants.
  if (
    !isInvitedSection &&
    !applicationsExist &&
    sectionType !== uiApplicationStatusTypes.COMPLETED &&
    sectionType !== uiApplicationStatusTypes.RECEIVED
  ) {
    return null;
  }

  return (
    <>
      <ApplicationsListTitle
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
        paddingBottom="sm"
        data-test="ApplicationsList__Title"
      >
        <Heading level="4" marginTop="sm">
          {sectionType.label}
        </Heading>
        {isInvitedSection && (
          <>
            <InviteToApplyLinks onInviteClickAnalyticsCallback={() => emitSendApplicationLinkEvent(listing, user)} />
            <InlineFeedback marginTop="md">
              For a group, invite one member of the group to apply. On the application form, they will have the
              opportunity to invite the other members.
            </InlineFeedback>
            <Paragraph fontType="finePrint" marginTop="sm">
              By enabling or sharing applications, you agree to comply with our{' '}
              <Anchor href={URLS.TERMS_OF_USE} target="_blank">
                Terms of Use
              </Anchor>
              ,{' '}
              <Anchor href={URLS.RENTAL_USER_TERMS} target="_blank">
                Rental User Terms
              </Anchor>
              , and{' '}
              <Anchor href={URLS.RESPECTFUL_RENTING_PLEDGE} target="_blank">
                Respectful Renting Pledge
              </Anchor>
              .
            </Paragraph>
          </>
        )}
        {titleCta ? titleCta : null}
      </ApplicationsListTitle>
      <ul>
        {applications.map((applicationId) => {
          const application = applicationMap[applicationId];
          return (
            <ListingApplicationsCardListItem key={applicationId}>
              <ListingApplicationCard application={application} />
            </ListingApplicationsCardListItem>
          );
        })}
      </ul>
      {isCompletedSection && applicationsDoNotExist && (
        <Paragraph marginBottom="md">{NO_COMPLETED_APPLICATIONS_MESSAGE}</Paragraph>
      )}
    </>
  );
}

ListingApplicationsApplicationSection.propTypes = {
  applications: PropTypes.array,
  applicationMap: PropTypes.object,
  sectionType: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
  }),
  titleCta: PropTypes.node,
};

ListingApplicationsApplicationSection.defaultProps = {
  titleCta: null,
};
